/**
 * Peer Valuation Score Gap Chart.
 *
 * Horizontal diverging bar chart showing target normalized radar scores minus
 * peer-average normalized radar scores for each valuation axis.
 */

import type Highcharts from "highcharts";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { createChartLifecycle, type ChartLifecycleReflowOptions } from "./chartLifecycle";
import { escapeHtml } from "./chartUtils";
import type { PeerChartData, PeerRadarData, PeerRadarMetric } from "./peerChartTypes";
import { showChartEmptyState } from "./peerChartTypes";

interface GapThemeColors {
  textColor: string;
  subtitleColor: string;
  gridLineColor: string;
  positiveColor: string;
  negativeColor: string;
  zeroLineColor: string;
  tooltipBg: string;
  tooltipBorder: string;
  dataLabelColor: string;
}

interface RadarGapPoint extends Highcharts.PointOptionsObject {
  y: number;
  color: string;
  custom: {
    metric: PeerRadarMetric;
    gap: number;
  };
}

const REQUIRED_RADAR_AXIS_COUNT = 3;

let themeObserver: MutationObserver | null = null;

function getThemeColors(): GapThemeColors {
  const dark = isDarkMode();
  return {
    textColor: dark ? "#E0E0E3" : "#333333",
    subtitleColor: dark ? "#A0A0A3" : "#666666",
    gridLineColor: dark ? "#374151" : "#e5e7eb",
    positiveColor: dark ? "#2dd4bf" : "#0d9488",
    negativeColor: dark ? "#fb7185" : "#e11d48",
    zeroLineColor: dark ? "#9ca3af" : "#6b7280",
    tooltipBg: dark ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.98)",
    tooltipBorder: dark ? "#404043" : "#cccccc",
    dataLabelColor: dark ? "#d1d5db" : "#4b5563",
  };
}

function formatSignedScore(value: number): string {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}`;
}

function renderChartForContainer(container: HTMLElement): void {
  const configAttr = container.dataset.chartConfig;
  if (!configAttr) {
    lifecycle.destroyChart(container.id);
    return;
  }

  try {
    const scriptEl = document.getElementById(configAttr);
    if (!scriptEl) {
      lifecycle.destroyChart(container.id);
      return;
    }

    const data = JSON.parse(scriptEl.textContent || "{}") as PeerChartData;
    if (!lifecycle.isContainerVisible(container)) {
      lifecycle.destroyChart(container.id);
      return;
    }

    lifecycle.destroyChart(container.id);
    const chart = renderPeerRadarGapChart(container, data.radarData);
    if (chart) {
      lifecycle.instances.set(container.id, chart);
    }
  } catch (e) {
    console.warn(`Failed to render peer radar gap chart for ${container.id}:`, e);
    showChartEmptyState(container, getThemeColors().textColor, "Chart could not be loaded");
  }
}

const lifecycle = createChartLifecycle(".peer-radar-gap-chart", renderChartForContainer, {
  deferUntilIntersect: true,
  rootMargin: "200px 0px",
});

function buildGapPoints(metrics: PeerRadarMetric[], colors: GapThemeColors): RadarGapPoint[] {
  return metrics.map((metric) => {
    const gap = Number((metric.targetScore - metric.peerAverageScore).toFixed(2));
    return {
      y: gap,
      color: gap >= 0 ? colors.positiveColor : colors.negativeColor,
      custom: {
        metric,
        gap,
      },
    };
  });
}

/**
 * Render the peer valuation score-gap chart.
 *
 * @param container - Target DOM element.
 * @param radarData - Chart-ready radar payload.
 * @returns Highcharts chart instance or null.
 */
export function renderPeerRadarGapChart(
  container: HTMLElement,
  radarData: PeerRadarData | undefined,
): Highcharts.Chart | null {
  if (!container || !window.Highcharts) return null;

  const colors = getThemeColors();
  container.style.height = "420px";

  if (
    !radarData ||
    radarData.status !== "success" ||
    radarData.metrics.length < REQUIRED_RADAR_AXIS_COUNT
  ) {
    showChartEmptyState(
      container,
      colors.textColor,
      radarData?.message || "Insufficient peer data to generate valuation comparison.",
    );
    return null;
  }

  const categories = radarData.metrics.map((metric) => metric.label);
  const gapPoints = buildGapPoints(radarData.metrics, colors);

  return window.Highcharts.chart(container, {
    chart: {
      type: "bar",
      backgroundColor: "transparent",
      spacing: [10, 15, 10, 10],
    },
    title: {
      text: "Valuation Score Gap",
      align: "left",
      style: { color: colors.textColor, fontSize: "14px", fontWeight: "600" },
    },
    subtitle: {
      text: "Target score minus peer average",
      align: "left",
      style: { color: colors.subtitleColor, fontSize: "11px" },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    xAxis: {
      categories,
      labels: {
        style: { color: colors.textColor, fontSize: "11px" },
      },
      lineColor: colors.gridLineColor,
    },
    yAxis: {
      min: -10,
      max: 10,
      tickInterval: 1,
      title: { text: null },
      gridLineColor: colors.gridLineColor,
      labels: {
        formatter() {
          return formatSignedScore(Number(this.value));
        },
        style: { color: colors.subtitleColor, fontSize: "10px" },
      },
      plotLines: [
        {
          value: 0,
          color: colors.zeroLineColor,
          width: 1,
          zIndex: 3,
        },
      ],
    },
    tooltip: {
      outside: true,
      useHTML: true,
      headerFormat: "",
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      style: { color: colors.textColor },
      formatter() {
        const point = this.point as Highcharts.Point & {
          custom?: RadarGapPoint["custom"];
        };
        const metric = point.custom?.metric;
        if (!metric) return "";
        const gap = point.custom?.gap ?? Number(this.y ?? 0);

        return (
          '<div style="padding:6px;min-width:220px;">' +
          `<strong>${escapeHtml(metric.label)}</strong>` +
          '<table style="width:100%;margin-top:6px;font-size:11px;">' +
          "<tbody>" +
          `<tr><td style="padding-right:10px;">Target raw</td><td style="text-align:right;"><b>${escapeHtml(metric.targetFormatted)}</b></td></tr>` +
          `<tr><td style="padding-right:10px;">Peer avg raw</td><td style="text-align:right;"><b>${escapeHtml(metric.peerAverageFormatted)}</b></td></tr>` +
          `<tr><td style="padding-right:10px;">Target score</td><td style="text-align:right;">${metric.targetScore.toFixed(2)}</td></tr>` +
          `<tr><td style="padding-right:10px;">Peer avg score</td><td style="text-align:right;">${metric.peerAverageScore.toFixed(2)}</td></tr>` +
          `<tr><td style="padding-right:10px;">Score gap</td><td style="text-align:right;"><b>${formatSignedScore(gap)}</b></td></tr>` +
          "</tbody></table>" +
          "</div>"
        );
      },
    },
    legend: { enabled: false },
    plotOptions: {
      bar: {
        borderRadius: 3,
        borderWidth: 0,
        animation: { duration: 400 },
        dataLabels: {
          enabled: true,
          formatter() {
            return formatSignedScore(Number(this.y ?? 0));
          },
          overflow: "allow",
          crop: false,
          padding: 3,
          style: {
            fontSize: "10px",
            fontWeight: "normal",
            textOutline: "none",
            color: colors.dataLabelColor,
          },
        },
      },
    },
    series: [
      {
        type: "bar",
        name: "Score gap",
        data: gapPoints,
      },
    ],
  } as Highcharts.Options) as Highcharts.Chart;
}

/** Initialize all peer radar gap charts on the page. */
export function initializePeerRadarGapCharts(
  root: ParentNode = document,
  options: ChartLifecycleReflowOptions = {},
): void {
  const containers = lifecycle.getContainers(root);
  const activeIds = new Set<string>();

  containers.forEach((container) => {
    if (!container.id) {
      container.id = `peer-radar-gap-${crypto.randomUUID().slice(0, 8)}`;
    }
    activeIds.add(container.id);
    lifecycle.reflowChartForContainer(container, options);
  });

  lifecycle.pruneStaleCharts(activeIds, root);

  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializePeerRadarGapCharts(document, { forceRender: true });
    });
  }
}

/** Register DOM listeners to auto-initialize peer radar gap charts. */
export function setupPeerRadarGapChartAutoInit(): void {
  lifecycle.setupAutoInit(initializePeerRadarGapCharts);
}
